import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as dropboxService from '../../../../services/dropbox';
import FolderSelector from '../Common/FolderSelector';

interface BulkCopyModalProps {
  selectedItems: Array<{ id: string; type: 'file' | 'folder'; name: string }>;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkCopyModal: React.FC<BulkCopyModalProps> = ({ selectedItems, onClose, onSuccess }) => {
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fileCount = selectedItems.filter(item => item.type === 'file').length;
  const folderCount = selectedItems.filter(item => item.type === 'folder').length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const response = await dropboxService.bulkCopy(selectedItems, targetFolderId);

      if (response?.success) {
        // Track file access for copied files
        // const fileItems = selectedItems.filter(item => item.type === 'file');
        // for (const file of fileItems) {
        //   try {
        //     await dropboxService.trackFileAccess(file.id, 'copied');
        //   } catch (trackError) {
        //     // Tracking error - fail silently
        //   }
        // }
        
        toast.success(`${selectedItems.length} item(s) copied successfully`);
        onSuccess();
        onClose();
      } else {
        toast.error(response?.message || 'Failed to copy items');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to copy items');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-copy mr-2"></i>
          Bulk Copy: Select Destination Folder
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="mb-3">
            <p className="mb-2">
              <strong>Copying {selectedItems.length} item(s):</strong>
            </p>
            <ul className="mb-0" style={{ fontSize: '0.9rem', color: '#5f6368' }}>
              {fileCount > 0 && <li>{fileCount} file(s)</li>}
              {folderCount > 0 && <li>{folderCount} folder(s)</li>}
            </ul>
          </div>

          <FolderSelector
            value={targetFolderId}
            onChange={setTargetFolderId}
            disabled={loading}
            label="Destination Folder"
            placeholder="Search or select destination folder..."
          />

          <div className="alert alert-info mt-3 mb-0" style={{ fontSize: '0.85rem' }}>
            <i className="fas fa-info-circle mr-2"></i>
            If items with the same name exist in the destination, they will be automatically renamed.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Copying...
              </>
            ) : (
              <>
                <i className="fas fa-copy mr-2"></i>
                Copy {selectedItems.length} Item(s)
              </>
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default BulkCopyModal;
